import { FirestoreService } from '../../../sdk/firestoreService';
import { LicenseService } from '../../../sdk/licenseService';
import { updateUserSuccess } from '../../AuthManager/actions/authActions';
import { uploadImage, deleteImage } from '../../ImageUploader/actions/imageUploaderActions';
import { v4 as uuidv4 } from 'uuid';

/**
 * onboardingActions - Actions עבור OnboardingWizardFlow
 * כל הלוגיקה של תהליך ההכנסה למערכת
 */

// ============================================
// Template Loading Actions
// ============================================

export const fetchTemplatesStart = () => ({
    type: 'FETCH_TEMPLATES_START'
});

export const fetchTemplatesSuccess = (templates) => ({
    type: 'FETCH_TEMPLATES_SUCCESS',
    payload: templates
});

export const fetchTemplatesFailure = (error) => ({
    type: 'FETCH_TEMPLATES_FAILURE',
    payload: error
});

/**
 * טעינת תבניות מפיירבייס
 * @returns {Promise<Array>} - מערך של תבניות
 */
export const fetchTemplates = () => async (dispatch) => {
    dispatch(fetchTemplatesStart());

    try {
        console.log('🔥 מתחיל לטעון תבניות מפיירבייס...');
        const fetchedTemplates = await FirestoreService.getPredefinedTemplates();

        // הוספת אופציה למסך ריק בתחילת הרשימה
        const allTemplates = [
            {
                id: 'empty',
                name: 'מסך ריק',
                description: 'התחל עם דשבורד ריק וצור את המסך שלך מאפס',
                widgets: [],
                settings: { bgColor: '#f0ece6', bgImage: '', borderRadius: 10 }
            },
            ...fetchedTemplates
        ];

        console.log('✅ נטענו', allTemplates.length, 'תבניות בהצלחה');
        dispatch(fetchTemplatesSuccess(allTemplates));
        return allTemplates;

    } catch (error) {
        console.error('❌ שגיאה בטעינת תבניות:', error);
        dispatch(fetchTemplatesFailure(error.message || 'שגיאה בטעינת תבניות'));

        // במקרה של שגיאה - החזר רק מסך ריק
        const emptyTemplate = [{
            id: 'empty',
            name: 'מסך ריק',
            description: 'התחל עם דשבורד ריק',
            widgets: [],
            settings: { bgColor: '#f0ece6', bgImage: '', borderRadius: 10 }
        }];

        dispatch(fetchTemplatesSuccess(emptyTemplate));
        return emptyTemplate;
    }
};

// ============================================
// Organization Creation Actions
// ============================================

// Action Creators
export const createOrganizationStart = () => ({
    type: 'CREATE_ORGANIZATION_START'
});

export const createOrganizationSuccess = () => ({
    type: 'CREATE_ORGANIZATION_SUCCESS'
});

export const createOrganizationFailure = (error) => ({
    type: 'CREATE_ORGANIZATION_FAILURE',
    payload: error
});

export const loadOrganizationSuccess = (organizationData) => ({
    type: 'LOAD_ORGANIZATION_SUCCESS',
    payload: organizationData
});

// יצירת hash קצר (5 תווים) מ-organizationId
const generateOrgHash = (orgId) => {
    let hash = 0;
    for (let i = 0; i < orgId.length; i++) {
        const char = orgId.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return Math.abs(hash).toString(36).substring(0, 5);
};

// ייצוא מחדש של uploadImage ו-deleteImage מ-ImageUploader
export { uploadImage, deleteImage };

/**
 * יצירת ארגון חדש עם דשבורד, מסך ופלייליסט ברירת מחדל
 * @param {object} user - המשתמש
 * @param {object} organizationData - נתוני הארגון
 * @param {object} template - תבנית דשבורד (אופציונלי)
 * @returns {Promise<object>} - {organizationId, dashboardId, screenId, playlistId}
 */
export const createOrganization = (user, organizationData, template = null) => async (dispatch) => {
    dispatch(createOrganizationStart());

    try {
        console.log('💾 שומר ארגון ב-Firestore:', organizationData);

        // 1. יצירת מזהה ארגון
        const organizationId = uuidv4();

        // 2. יצירת hash לארגון
        const orgHash = generateOrgHash(organizationId);
        console.log('🔑 Organization Hash:', orgHash);

        // 3. יצירת אובייקט ארגון מלא
        const fullOrganizationData = {
            ...organizationData,
            organizationId,
            orgHash,
            users: [user.userId]
        };

        // 4. שמירת הארגון ב-Firestore
        await FirestoreService.createOrganization(organizationId, fullOrganizationData);

        // 5. יצירת דשבורד ברירת מחדל (עם או בלי תבנית)
        console.log('📊 יוצר דשבורד ברירת מחדל...');

        // קביעת שם הדשבורד - אם יש תבנית עם שם (ולא מסך ריק), השתמש בשם התבנית
        const dashboardName = (template?.name && template.id !== 'empty') ? template.name : 'דשבורד';

        const dashboardOptions = {
            name: dashboardName,
            description: 'דשבורד ברירת מחדל',
            createInitialVersion: false  // לא יוצג עד publish
        };

        // אם יש תבנית עם ווידג'טים (מסך ריק), העבר אותם
        // אם widgets הוא null (מסך מלא), השתמש בווידג'טים ברירת המחדל
        if (template && template.widgets !== null) {
            console.log('🎨 משתמש בתבנית:', template.name || 'תבנית מותאמת');
            dashboardOptions.templateWidgets = template.widgets;
            dashboardOptions.templateSettings = template.settings;
        } else {
            console.log('🎨 משתמש בווידג\'טים ברירת מחדל (מסך מלא)');
        }

        const dashboard = await FirestoreService.createDashboard(organizationId, dashboardOptions);

        // 5.1 פרסום הדשבורד הראשון אוטומטית
        console.log('📢 מפרסם דשבורד ברירת מחדל...');
        const publishPromise = FirestoreService.updateDashboardNew(dashboard.dashboardId, {
            versions: [
                {
                    versionId: uuidv4(),
                    widgets: dashboard.current.widgets,
                    settings: dashboard.current.settings,
                    publishedAt: new Date(),
                    publishedBy: user.userId
                }
            ]
        });

        // 6-10. יצירת מסך, פלייליסט ו-schedule במקביל להכנה
        console.log('🖥️ מכין מסך ופלייליסט...');
        const screenId = uuidv4();
        const defaultScreenUrl = 'main-screen';

        // יצירת schedule ברירת מחדל - כל יום עם אותו פלייליסט 24/7
        // (נשתמש בזה אחרי שיצרנו את הפלייליסט)
        const createScreenAndPlaylist = async () => {
            // יצירת פלייליסט
            const playlist = await FirestoreService.createPlaylist(organizationId, {
                name: 'מערך תצוגה ראשי',
                description: 'מערך תצוגה ברירת מחדל',
                items: [
                    {
                        dashboardId: dashboard.dashboardId,
                        duration: 30 // 30 שניות
                    }
                ]
            }, screenId);

            // יצירת schedule עם הפלייליסט
            const defaultSchedule = {};
            for (let day = 0; day < 7; day++) {
                defaultSchedule[day] = [
                    {
                        playlistId: playlist.playlistId,
                        startTime: '00:00',
                        endTime: '23:59'
                    }
                ];
            }

            // יצירת המסך
            const screen = await FirestoreService.createScreenWithId(screenId, organizationId, {
                name: 'מסך ראשי',
                description: 'מסך ברירת מחדל',
                url: defaultScreenUrl,
                schedule: defaultSchedule
            });

            return { screen, playlist };
        };

        // 11. הרצת פרסום הדשבורד ועדכון המשתמש במקביל ליצירת המסך והפלייליסט
        const [{ screen, playlist }] = await Promise.all([
            createScreenAndPlaylist(),
            publishPromise,
            FirestoreService.updateUser(user.userId, { organizationId })
        ]);

        // 12. יצירת רישיון Trial אוטומטי לארגון החדש
        console.log('📋 יוצר רישיון Trial לארגון...');
        const license = await LicenseService.createTrialLicense(organizationId, user.userId);
        dispatch({ type: 'FETCH_LICENSE_SUCCESS', payload: license });

        // 13. עדכון ה-state
        dispatch(updateUserSuccess({ organizationId }));
        dispatch(loadOrganizationSuccess(fullOrganizationData));
        dispatch(createOrganizationSuccess());

        console.log('🎉 ארגון נוצר בהצלחה עם מסך, פלייליסט ודשבורד!');
        console.log('📦 Organization ID:', organizationId);
        console.log('🔑 Organization Hash:', orgHash);
        console.log('📦 Screen ID:', screen.screenId);
        console.log('🔗 Screen URL:', defaultScreenUrl);
        console.log('📦 Playlist ID:', playlist.playlistId);
        console.log('📦 Dashboard ID:', dashboard.dashboardId);

        // החזרת הנתונים החשובים
        return {
            organizationId,
            dashboardId: dashboard.dashboardId,
            screenId: screen.screenId,
            playlistId: playlist.playlistId
        };

    } catch (error) {
        console.error('❌ שגיאה ביצירת ארגון:', error);
        dispatch(createOrganizationFailure('Failed to create organization'));
        throw error;
    }
};

/**
 * השלמת תהליך ההכנסה - יצירת ארגון וניווט לדשבורד
 * @param {object} user - המשתמש
 * @param {object} organizationData - נתוני הארגון
 * @param {object} selectedTemplate - התבנית הנבחרת
 * @param {function} navigate - פונקציית ניווט
 * @returns {Promise<object>} - {success, organizationId, dashboardId, error}
 */
export const completeOnboarding = (user, organizationData, selectedTemplate, navigate) => async (dispatch) => {
    try {
        if (!user) {
            throw new Error('אין משתמש מחובר');
        }

        // יצירת ארגון עם התבנית הנבחרת
        const result = await dispatch(createOrganization(user, organizationData, selectedTemplate));

        if (!result?.organizationId || !result?.dashboardId) {
            throw new Error('לא התקבלו נתונים מיצירת הארגון');
        }

        // המתן קצר כדי ש-Redux יתעדכן
        await new Promise(resolve => setTimeout(resolve, 300));

        // ניווט לדשבורד
        const dashboardUrl = `/${result.organizationId}/dashboard/${result.dashboardId}`;
        navigate(dashboardUrl);

        return {
            success: true,
            organizationId: result.organizationId,
            dashboardId: result.dashboardId
        };
    } catch (error) {
        console.error('❌ שגיאה בהשלמת תהליך ההכנסה:', error);
        return {
            success: false,
            error: error.message || 'שגיאה ביצירת הארגון'
        };
    }
};
